import React from "react";
import { Box } from "@mui/material";
import SearchableInput from "components/SearchableInput";
import { useSnackbar } from "hooks/useSnackbar";
import { getPlayerCards, playerCardInfo } from "services/playerCardService";

export const FTUE_SHOWCASE_SLOT_COUNT = 3;
const PAGE_SIZE = 20;

type Option = { id: string; label: string };

// Includes position & OVR (on top of the usual name/nationality/club) since duplicate
// versions of the same player are otherwise indistinguishable in the dropdown.
const buildCardLabel = (card: any) =>
  `${card?.fullName} - ${card?.nationality} - ${card?.club} - ${card?.preferredPosition} - OVR ${card?.overallRating}`;

interface NewFtueConfigProps {
  value?: (string | null)[];
  onChange: (ids: (string | null)[]) => void;
}

interface ShowcaseCardSlotProps {
  index: number;
  selected: Option | null;
  initialOptions: Option[];
  initialLoading: boolean;
  excludeIds: string[];
  onSelect: (option: Option | null) => void;
}

const toOption = (card: any): Option => ({ id: card?._id, label: buildCardLabel(card) });

// A single searchable dropdown. Owns its search results and loading state, so searching one
// slot doesn't affect the others. With no search text it shows the shared first page.
const ShowcaseCardSlot = ({
  index,
  selected,
  initialOptions,
  initialLoading,
  excludeIds,
  onSelect,
}: ShowcaseCardSlotProps) => {
  const { showSnackbar } = useSnackbar();
  const [searchOptions, setSearchOptions] = React.useState<Option[] | null>(null);
  const [loading, setLoading] = React.useState(false);

  const onSearch = React.useCallback(async (search: string) => {
    if (!search) {
      setSearchOptions(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await getPlayerCards({ pageNo: 1, pageSize: PAGE_SIZE, search, skipPagination: true });
      setSearchOptions((resp?.items ?? []).filter((c: any) => !c?.isDisabled).map(toOption));
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const listed = (searchOptions ?? initialOptions).filter(
    (o) => o.id === selected?.id || !excludeIds.includes(o.id),
  );
  // MUI needs the selected value among the options, and a saved card can sit outside the fetched page.
  const options =
    selected && !listed.some((o) => o.id === selected.id) ? [selected, ...listed] : listed;

  return (
    <SearchableInput
      name={`showcasePlayerCard${index}`}
      label={`Showcase Player Card ${index + 1}`}
      placeholder="Search player card"
      selected={selected}
      options={options}
      onInputChange={onSearch}
      onOptionSelect={(e: any) => onSelect(e?.target?.value ?? null)}
      loading={loading || initialLoading}
    />
  );
};

// Three ordered showcase player card slots - slot N maps to showcasePlayerCardIds[N].
const NewFtueConfig = ({ value, onChange }: NewFtueConfigProps) => {
  const { showSnackbar } = useSnackbar();
  const [initialOptions, setInitialOptions] = React.useState<Option[]>([]);
  const [initialLoading, setInitialLoading] = React.useState(false);
  const [labels, setLabels] = React.useState<Record<string, string>>({});

  const ids = Array.from({ length: FTUE_SHOWCASE_SLOT_COUNT }, (_, i) => value?.[i] ?? null);

  // First page is fetched once and shared by all slots.
  React.useEffect(() => {
    let active = true;
    const fetchInitial = async () => {
      setInitialLoading(true);
      try {
        const resp = await getPlayerCards({ pageNo: 1, pageSize: PAGE_SIZE });
        if (active) setInitialOptions((resp?.items ?? []).filter((c: any) => !c?.isDisabled).map(toOption));
      } catch (error) {
        if (active) showSnackbar(error.message, "error");
      } finally {
        if (active) setInitialLoading(false);
      }
    };
    fetchInitial();
    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve labels for saved ids that are not in the first page of results.
  const savedKey = ids.join(",");
  React.useEffect(() => {
    const known = new Set(initialOptions.map((o) => o.id));
    const missing = ids.filter((id) => id && !labels[id] && !known.has(id));
    if (!missing.length) return;
    let active = true;
    Promise.all(missing.map((id) => playerCardInfo(id).catch(() => null))).then((cards) => {
      if (!active) return;
      setLabels((prev) => ({
        ...prev,
        ...Object.fromEntries(
          missing.map((id, i) => [id, cards[i] ? buildCardLabel(cards[i]) : id]),
        ),
      }));
    });
    return () => {
      active = false;
    };
  }, [savedKey, initialOptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const labelFor = (id: string) =>
    labels[id] ?? initialOptions.find((o) => o.id === id)?.label ?? id;

  // Memoized because SearchableInput resets its typed text whenever `selected` changes identity.
  const selectedLabelsKey = ids.map((id) => (id ? labelFor(id) : "")).join("|");
  const selectedOptions = React.useMemo(
    () => ids.map((id) => (id ? { id, label: labelFor(id) } : null)),
    [savedKey, selectedLabelsKey], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onSlotSelect = (index: number) => (option: Option | null) => {
    if (option) setLabels((prev) => ({ ...prev, [option.id]: option.label }));
    const next = [...ids];
    next[index] = option?.id ?? null;
    onChange(next);
  };

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      {ids.map((_, index) => (
        <ShowcaseCardSlot
          key={index}
          index={index}
          selected={selectedOptions[index]}
          initialOptions={initialOptions}
          initialLoading={initialLoading}
          // Hide cards already picked in the other slots
          excludeIds={ids.filter((other, i) => i !== index && other) as string[]}
          onSelect={onSlotSelect(index)}
        />
      ))}
    </Box>
  );
};

export default NewFtueConfig;
